import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { statuses } from '../../services/apiService';
import { getStatusTypeOptions, getDepartureKindOptions } from '../../utils/talentTypeUtils';
import { TextField, Checkbox, Autocomplete } from '@anyemp/ui-kit';
import { DEFAULT_STATUS_COLOR } from '../../utils/colorUtils';

const StatusForm = ({ initialData = null, onSubmit, onCancel, submitLabel = "Create" }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    talent_type: 'talent',
    departure_kind: '',
    color: DEFAULT_STATUS_COLOR,
    is_active: true,
    public_visible: true,
    library_status_id: null
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        talent_type: initialData.talent_type || 'talent',
        departure_kind: initialData.departure_kind || '',
        color: initialData.color || DEFAULT_STATUS_COLOR,
        is_active: initialData.is_active !== undefined ? initialData.is_active : true,
        public_visible: initialData.public_visible !== undefined ? initialData.public_visible : true,
        library_status_id: initialData.library_status_id || null
      });
    }
  }, [initialData]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Очищаем ошибку для этого поля
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.talent_type) {
      newErrors.talent_type = 'Status type is required';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        await onSubmit({
          ...formData,
          departure_kind: formData.talent_type === 'talent' && formData.departure_kind ? formData.departure_kind : null
        });
        toast.success(initialData ? 'Status updated successfully!' : 'Status created successfully!');
      } catch (error) {
        // Error already handled in parent component
        console.error('Form submission error:', error);
      }
    } else {
      toast.error('Please fix the errors in the form');
    }
  };

  const talentTypes = getStatusTypeOptions();


  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Status Information (read-only) */}
      {initialData && (
        <div className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg p-4 mb-4">
          <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">Status Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">
                Status Name
              </label>
              <div className="text-light-text-primary dark:text-dark-text-primary font-medium" title={formData.name}>
                {formData.name}
              </div>
            </div>

            {/* Color and Library Status ID in one row */}
            <div className="flex gap-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">
                  Color
                </label>
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 border border-light-border-primary dark:border-dark-border-primary rounded shadow-sm"
                    style={{ backgroundColor: formData.color }}
                  ></div>
                  <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary font-mono">{formData.color}</span>
                </div>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">
                  Library Status ID
                </label>
                <div className="text-light-text-primary dark:text-dark-text-primary font-mono text-sm" title={formData.library_status_id || 'Not specified'}>
                  {formData.library_status_id || 'Not specified'}
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-2">
            * Name, color and Library Status ID are synchronized with external microservice and cannot be changed
          </p>
        </div>
      )}

      <div className="max-w-md">
        {/* Тип сущности: у статуса таланта своего типа таланта нет — к каким
            типам (candidate, employee ...) он относится, задают его стадии */}
        <Autocomplete
          fullWidth
          label="Status Type *"
          options={talentTypes}
          value={formData.talent_type}
          onChange={(value) => {
            setFormData(prev => ({
              ...prev,
              talent_type: value || ''
            }));
            // Очищаем ошибку для этого поля
            if (errors.talent_type) {
              setErrors(prev => ({
                ...prev,
                talent_type: ''
              }));
            }
          }}
          placeholder="Select status type..."
          error={!!errors.talent_type}
          helperText={errors.talent_type}
        />
        {formData.talent_type === 'talent' && (
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1">
            A talent status is available on the stages it is linked to — manage that in Stage settings.
          </p>
        )}
      </div>

      {formData.talent_type === 'talent' && (
        <div className="max-w-md">
          <Autocomplete
            clearable
            fullWidth
            label="Departure Status"
            options={getDepartureKindOptions()}
            value={formData.departure_kind}
            onChange={(value) => setFormData(prev => ({ ...prev, departure_kind: value || '' }))}
            placeholder="Not a departure status"
          />
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1">
            When a "Fired" or "Left" important date comes, the talent gets the matching departure status of their stage.
          </p>
        </div>
      )}

      {/* Description */}
      <div className="max-w-2xl">
        <TextField
          label="Description"
          id="description"
          name="description"
          multiline
          rows={4}
          fullWidth
          value={formData.description}
          onChange={handleChange}
          placeholder="Status description (optional)"
        />
      </div>


      {/* Checkboxes */}
      <div className="flex flex-wrap gap-6 max-w-lg">
        <Checkbox
          label="Active"
          color="primary"
          size="small"
          checked={!!formData.is_active}
          onChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
        />
        <Checkbox
          label="Publicly Visible"
          color="primary"
          size="small"
          checked={!!formData.public_visible}
          onChange={(checked) => setFormData(prev => ({ ...prev, public_visible: checked }))}
        />
      </div>

      {/* Buttons */}
      <div className="flex flex-col-reverse gap-3 pt-4 sm:flex-row sm:flex-wrap sm:justify-end [&>*]:w-full sm:[&>*]:w-auto">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-light-text-secondary dark:text-dark-text-secondary bg-light-bg-tertiary dark:bg-dark-bg-tertiary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded-md transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
};

export default StatusForm;
